import { Link, useLocation } from "wouter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Property } from "@/types/property";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { isUnauthorizedError } from "@/lib/authUtils";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import API_URL from "@/config";
import axiosInstance from "@/lib/axiosInstance";
import { formatDate, formatPrice } from "@/lib/utils";

interface PropertyCardProps {
  property: Property;
  showFavoriteButton?: boolean;
}

export default function PropertyCard({ property, showFavoriteButton = false }: PropertyCardProps) {
  const [_, navigate] = useLocation();
  const { user } = useSelector((state: any) => state.auth);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  showFavoriteButton = user?.userType != "seller"

  const { data: isFavoriteData } = useQuery<{ isFavorite: boolean }>({
    queryKey: ["/api/favorites", property._id, "status"],
    queryFn: async () => {
      if (!user) return { isFavorite: false };
      if (user?.userType == "seller") return { isFavorite: false };
      const res = await axiosInstance.get(`/api/favorites/${property._id}/status`);
      return res.data;
    },
    enabled: !!user && showFavoriteButton,
  });

  const favoriteMutation = useMutation({
    mutationFn: async (action: 'add' | 'remove') => {
      if (action === 'add') {
        return await axiosInstance.post("/api/favorites", { propertyId: property._id });
      } else {
        return await axiosInstance.delete(`/api/favorites/${property._id}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/favorites"] });
      queryClient.invalidateQueries({ queryKey: ["/api/favorites", property._id, "status"] });
      toast({
        variant: "success",
        title: "Success",
        description: isFavoriteData?.isFavorite ? "Removed from favorites" : "Added to favorites",
      });
    },
    onError: (error : any) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          navigate("/login")
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to update favorites",
        variant: "destructive",
      });
    },
  });

  const handleFavoriteToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      navigate("/login")
      return;
    }
    if(user?.userType == "seller"){
      return
    }
    
    favoriteMutation.mutate(isFavoriteData?.isFavorite ? 'remove' : 'add');
  };

  const mainImage = property.images?.[0] ? `${API_URL}/getImage/image/${property.images?.[0]}` : "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=800&h=600";

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" data-testid={`card-property-${property._id}`}>
      <Link href={`/property/${property._id}`}>
        <div className="relative">
          <img 
            src={mainImage}
            alt={property.title}
            className="w-full h-48 object-cover"
            data-testid={`img-property-${property._id}`}
          />
          
          {showFavoriteButton && (
            <button
              onClick={handleFavoriteToggle}
              disabled={favoriteMutation.isPending}
              className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-gray-50 transition-colors"
              data-testid={`button-favorite-${property._id}`}
            >
              <i className={`${isFavoriteData?.isFavorite ? 'fas text-red-500' : 'far text-gray-600'} fa-heart`}></i>
            </button>
          )}
          
          {property.isFeatured && (
            <div className="absolute bottom-3 left-3 bg-primary text-white px-2 py-1 rounded text-sm font-medium">
              Featured
            </div>
          )}
          
          {property.createdAt && new Date(property.createdAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) && (
            <div className="absolute bottom-3 left-3 bg-primary text-white px-2 py-1 rounded text-sm font-medium">
              New Listing
            </div>
          )}
        </div>
        
        <div className="p-4">
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-lg font-semibold text-gray-900" data-testid={`text-price-${property._id}`}>
              {formatPrice(property.price)}
            </h3>
            <span className="text-sm text-gray-500 capitalize" data-testid={`text-type-${property._id}`}>
              {property.propertyType}
            </span>
          </div>
          
          <p className="text-gray-600 mb-3" data-testid={`text-address-${property._id}`}>
            {property.city}, {property.county}
          </p>
          
          <div className="flex items-center text-sm text-gray-500 mb-3">
            {property.beds !== null && property.beds !== undefined && (
              <span className="mr-4" data-testid={`text-bedrooms-${property._id}`}>
                <i className="fas fa-bed mr-1"></i>{property.beds} bed
              </span>
            )}
            {property.baths !== null && property.baths !== undefined && (
              <span className="mr-4" data-testid={`text-bathrooms-${property._id}`}>
                <i className="fas fa-bath mr-1"></i>{property.baths} bath
              </span>
            )}
            {property.size && (
              <span data-testid={`text-area-${property._id}`}>
                <i className="fas fa-ruler-combined mr-1"></i>{property.size} m²
              </span>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-500">
              {property?.agent?.image ? (
                <img 
                  src={property?.agent?.image} 
                  alt={property?.agent?.name || "Agent"}
                  className="w-6 h-6 rounded-full object-cover mr-2"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-2">
                  <i className="fas fa-user text-xs text-gray-400"></i>
                </div>
              )}
              <span data-testid={`text-agent-${property._id}`}>
                {property.agent?.name || "PropertyHub"}
              </span>
            </div>
            
            {property.createdAt && (
              <span className="text-xs text-gray-400" data-testid={`text-date-${property._id}`}>
                {formatDate(property.createdAt)}
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}
